// Ops desk modules, row 2: the agent-health grid (span 7) and the signal
// pipeline funnel (span 5). Reads go through opsModuleHooks, which share the
// key/queryFn/refetchInterval of the panel behind each drawer, so opening the
// drawer is a cache hit. Faces are state-only: nothing clickable inside.
//
// Loading rule (see fleetModuleHooks): `stillLoading` shows the skeleton, a query
// that failed with nothing to show gets the error rung, and a failed REFETCH
// keeps its cached data.
import { HeatGrid } from '../viz/HeatGrid'
import { MiniFunnel } from '../viz/MiniFunnel'
import { Module } from './Module'
import { stillLoading, unavailable } from './fleetModuleHooks'
import { useAgentHealth24h, useRuntimeAgents, useSignalPipeline } from './opsModuleHooks'
import { NOT_CONFIGURED, agentCells, pipelineFace } from './opsSummaries'
import './OpsModules.css'

// ------------------------------------------------------------- agent-health

const AGENT_FOOT = '24h totals · hourly buckets not yet surfaced'

/**
 * One cell per agent from two reads (ruling 42): the runtime agents and the 24h
 * decision agents. Either may fail on its own: the grid then shows the other's
 * agents and the foot names the missing source. The error rung needs BOTH down.
 */
export function AgentHealthModule() {
  const runtime = useRuntimeAgents()
  const decisions = useAgentHealth24h()
  const runtimeDown = unavailable(runtime)
  const decisionsDown = unavailable(decisions)

  const cells = agentCells(runtimeDown ? undefined : runtime.data, decisionsDown ? undefined : decisions.data)
  const missing = [runtimeDown && 'runtime agents', decisionsDown && '24h decisions'].filter(Boolean)
  // An empty grid with a failed source is not "no agents reported": the failed
  // source may be the one that has them. Both down keeps the generic text.
  const bothDown = runtimeDown && decisionsDown
  const errorText = bothDown ? 'agents unavailable' : `${missing.join(', ')} unavailable`
  const healthy = cells.filter((c) => c.value !== -1 && c.display !== NOT_CONFIGURED).length
  const foot = missing.length > 0 ? `${AGENT_FOOT} · ${missing.join(', ')} unavailable` : AGENT_FOOT

  return (
    <Module
      title="Agent health"
      caption="24h"
      drawerKey="agent-health"
      span={7}
      isLoading={stillLoading(runtime) || stillLoading(decisions)}
      isError={bothDown || (cells.length === 0 && missing.length > 0)}
      errorText={errorText}
      isEmpty={cells.length === 0}
      emptyText="no agents reported"
    >
      <div className="ops-agent-heat">
        <HeatGrid
          cells={cells}
          columns={3}
          maxAbs={1}
          ariaLabel={`${healthy} of ${cells.length} agents healthy`}
        />
      </div>
      <p className="module-foot">{foot}</p>
    </Module>
  )
}

// ---------------------------------------------------------- signal-pipeline

/** The cumulative or current-session funnel (ruling 48), one foot line per fact. */
export function SignalPipelineModule() {
  const q = useSignalPipeline()
  const face = pipelineFace(q.data)
  const count = (i: number) => face?.stages[i]?.count ?? 0

  return (
    <Module
      title="Signal pipeline"
      caption="default book"
      drawerKey="signal-pipeline"
      span={5}
      isLoading={stillLoading(q)}
      isError={unavailable(q)}
      errorText="pipeline unavailable"
    >
      {face && (
        <>
          <MiniFunnel
            stages={face.stages}
            ariaLabel={`${face.window} pipeline: ${count(0)} received, ${count(3)} executed, ${count(4)} rejected`}
          />
          {face.foot.map((line) => (
            <p className="module-foot" key={line}>{line}</p>
          ))}
        </>
      )}
    </Module>
  )
}
